import { defineComponent as _defineComponent } from 'vue'
import { queries } from "@/apollo";
import { StakeFiles, User } from "@/CodegenTypes";
import { first, sumBy } from "lodash";
import { formatDollar } from "@/helpers/formatDollar";
import { customMoment } from "@/main";

import Clusterize from "clusterize.js";

import pdfMake from "pdfmake";
import pdfMakeFont from "pdfmake/build/vfs_fonts";
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router/composables";
import { useQuery } from "@vue/apollo-composable";
import NumberStakeModal from "@/components/modules/bet-details/NumberStakeModal.vue";
import { formatCount } from "@/helpers/formatCount";
import { useTranslation } from "@/store/translation";

type SortType =
  | "number-asc"
  | "number-desc"
  | "amount-asc"
  | "amount-desc"
  | "big-asc"
  | "big-desc"
  | "small-asc"
  | "small-desc";

type StakeFileRow = {number: string, big: number, small: number};
type StakeFileTypes = keyof Pick<StakeFiles, "masterStake" | "downlineEatStake" | "companyFinalStake" | "outsetStake">;


export default /*#__PURE__*/_defineComponent({
  __name: 'StakeFile',
  setup(__props) {

const route = useRoute();

const { $t } = useTranslation();

const vfs = (pdfMakeFont as any)?.pdfMake?.vfs ?? (pdfMakeFont as any)?.vfs;


const selectedNumber = ref<string>();
const showNumberStakeModal = ref(false);

const selectedStartReleaseDate = computed(() => {
  const query = route.query.startReleaseDate;
  return Array.isArray(query) ? first(query) : query;
});

const selectedStartReleaseDateDate = computed<Date | undefined>(() => {
  const value = selectedStartReleaseDate.value;
  return value ? customMoment(value).toDate() : undefined;
});

const { result, loading, refetch } = useQuery<{
  stakeFile: {
    masterStake: StakeFileRow[];
  downlineEatStake: StakeFileRow[];
  companyFinalStake: StakeFileRow[];
  outsetStake: StakeFileRow[];
  }

}>(queries.stakeFile, computed(() => ({
  releaseDate: selectedStartReleaseDate.value,
})), {
  enabled: computed(() => !!selectedStartReleaseDate.value),
});

watch(selectedStartReleaseDate, () => {
  console.log("selectedStartReleaseDate changed", selectedStartReleaseDate.value)
  if (selectedStartReleaseDate.value) {
    refetch({ releaseDate: selectedStartReleaseDate.value});
  }
});

const loadedAt = ref(new Date());

const {
  result: meResult,
  refetch: refetchMe,
  loading: loadingMe,
} = useQuery<{ me: User }>(queries.me, {}, { fetchPolicy: "cache-first" });

const me = computed(() => {
  return meResult.value?.me;
})


const numberFilter = ref("");

const scrollArea = ref<HTMLDivElement | null>(null);
const contentArea = ref<HTMLTableSectionElement | null>(null);

const generatingPdf = ref(false);

let clusterize = null as Clusterize | null;

const sortMode = ref<SortType>("number-asc");

const selectedBetTypeId = ref<StakeFileTypes>("companyFinalStake");

const betTypes: { name: string; id: StakeFileTypes }[] = [
  { name: "Master Bets", id: "masterStake" },
  { name: "Downline Intake", id: "downlineEatStake" },
  { name: "Company Out", id: "outsetStake" },
  { name: "Company Final", id: "companyFinalStake" },
];

const stakeFile = computed(() => {
  return result.value?.stakeFile;
});

const selectedBetType = computed(() => {
  return betTypes.find((bt) => bt.id === selectedBetTypeId.value);
});

const selectedStake = computed(() => {
  const selectedStake = stakeFile.value?.[selectedBetTypeId.value];

  let stake = selectedStake;
  if (numberFilter.value) {
    stake = selectedStake?.filter((s) => s.number.includes(numberFilter.value));
  }
  return stake
    ?.map((s) => ({
      ...s,
      amount: s.big * 1.6 + s.small * 0.7,
    }))
    .filter((s) => s.big || s.small || s.amount);
});

const sortedRows = computed(() => {
  if (!selectedStake.value) {
    return [];
  }
  let rowsDataSorted = selectedStake.value;
  if (sortMode.value === "number-asc") {
    rowsDataSorted = rowsDataSorted.sort((a, b) => a.number.localeCompare(b.number));
  } else if (sortMode.value === "number-desc") {
    rowsDataSorted = rowsDataSorted.sort((a, b) => b.number.localeCompare(a.number));
  } else if (sortMode.value === "amount-asc") {
    rowsDataSorted = rowsDataSorted.sort((a, b) => a.amount - b.amount);
  } else if (sortMode.value === "amount-desc") {
    rowsDataSorted = rowsDataSorted.sort((a, b) => b.amount - a.amount);
  } else if (sortMode.value === "big-asc") {
    rowsDataSorted = rowsDataSorted.sort((a, b) => a.big - b.big);
  } else if (sortMode.value === "big-desc") {
    rowsDataSorted = rowsDataSorted.sort((a, b) => b.big - a.big);
  } else if (sortMode.value === "small-asc") {
    rowsDataSorted = rowsDataSorted.sort((a, b) => a.small - b.small);
  } else if (sortMode.value === "small-desc") {
    rowsDataSorted = rowsDataSorted.sort((a, b) => b.small - a.small);
  }
  return rowsDataSorted;
});

const renderedRows = computed(() => {
  const rows = sortedRows.value?.map((row) => {
    return `
      <tr>
        <td class="text-center">
          <a
            id="${row.number}"
            class="font-bold text-primary-600 cursor-pointer number-row"
            >${row.number}</a
          >
        </td>
        <td>${formatCount(row.big)}</td>
        <td>${formatCount(row.small)}</td>
        <td>${formatCount(row.amount)}</td>
        </tr>
        `;
  });
  return rows;
});

watch(renderedRows, doClusterize, { immediate: true });
function doClusterize() {
  if (clusterize) {
    clusterize?.update(renderedRows.value);
  } else {
    if (!scrollArea.value || !contentArea.value) {
      return;
    }
    clusterize = new Clusterize({
      rows: renderedRows.value,
      show_no_data_row: false,
      scrollElem: scrollArea.value,
      contentElem: contentArea.value,
      rows_in_block: 20,
      callbacks: {
          clusterChanged: () => {
            const numberRows = document.getElementsByClassName("number-row");
            for (const row of numberRows) {
              row.addEventListener("click", () => {
                handleSelectNumber(row.id);
              });
            }
          },
        },
    });
  }
}

function handleSelectNumber(number: string) {
    selectedNumber.value = number;
    showNumberStakeModal.value = true;
  }

const stakeTotal = computed(() => {
  if (!stakeFile.value) {
    return null;
  }
  return {
    masterStake: {
      big: sumBy(stakeFile.value.masterStake, (d) => d.big),
      small: sumBy(stakeFile.value.masterStake, (d) => d.small),
      count: Object.values(stakeFile.value.masterStake).filter(
        (v) => v.big || v.small
      ).length,
      amount: sumBy(
        stakeFile.value.masterStake,
        (d) => d.big * 1.6 + d.small * 0.7
      ),
    },
    downlineEatStake: {
      big: sumBy(stakeFile.value.downlineEatStake, (d) => d.big),
      small: sumBy(stakeFile.value.downlineEatStake, (d) => d.small),
      count: Object.values(stakeFile.value.downlineEatStake).filter(
        (v) => v.big || v.small
      ).length,
      amount: sumBy(
        stakeFile.value.downlineEatStake,
        (d) => d.big * 1.6 + d.small * 0.7
      ),
    },
    companyFinalStake: {
      big: sumBy(stakeFile.value.companyFinalStake, (d) => d.big),
      small: sumBy(stakeFile.value.companyFinalStake, (d) => d.small),
      count: Object.values(stakeFile.value.companyFinalStake).filter(
        (v) => v.big || v.small
      ).length,
      amount: sumBy(
        stakeFile.value.companyFinalStake,
        (d) => d.big * 1.6 + d.small * 0.7
      ),
    },
    outsetStake: {
      big: sumBy(stakeFile.value.outsetStake, (d) => d.big),
      small: sumBy(stakeFile.value.outsetStake, (d) => d.small),
      count: Object.values(stakeFile.value.outsetStake).filter(
        (v) => v.big || v.small
      ).length,
      amount: sumBy(
        stakeFile.value.outsetStake,
        (d) => d.big * 1.6 + d.small * 0.7
      ),
    },
  } as const;
});

function exportCsv() {
  const selected = stakeFile.value?.[selectedBetTypeId.value];
  if (!selected) {
    return;
  }
  let exportText = ``;

  exportText += `,,,This file is for analyze use only. As CSV/Excel files can be edited it cannot be used as proof. \r\n`;
  exportText += `,,,We will not take responsibility for data in CSV/Excel downloads. \r\n`;
  exportText += `,,,To download stake proof use Download Pdf. \r\n`;
  exportText += `Number,Big,Small\r\n`;

  selected.forEach((row) => {
    exportText += `${row.number},${row.big.toFixed(2)},${row.small.toFixed(2)}`;
    exportText += `\r\n`;
  });
  const data = new Blob([exportText], { type: "text/plain" });

  // If we are replacing a previously generated file we need to
  // manually revoke the object URL to avoid memory leaks.
  // if (textFile !== null) {
  //   window.URL.revokeObjectURL(textFile);
  // }

  const textFileUrl = window.URL.createObjectURL(data);

  const link = document.createElement("a");
  link.href = textFileUrl;
  const betTypeName = betTypes.find((bt) => bt.id === selectedBetTypeId.value);
  const fileName = betTypeName?.name || "stake";
  link.setAttribute("download", fileName + ".csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function exportPdf() {
  if (!selectedStake.value || !selectedStartReleaseDate.value || !me.value) {
    return;
  }
  const fileName = selectedBetType.value?.name;
  const releaseDateStr = formatDate(selectedStartReleaseDate.value);
  // const reportType = this.selectedRelease.locked
  //   ? "Final"
  //   : "Bet In Progress";
  const docDef = {
    pageMargins: [40, 60, 40, 60],
    pageSize: {
      width: 595.28,
      height: 191200,
    },
    watermark: {
      text: `${releaseDateStr}`,
      opacity: 0.1,
    },
    header: {
      columns: [
        { text: `${fileName}` },
        { text: `Draw: ${releaseDateStr}`, alignment: "right" },
      ],
      margin: [40, 20],
    },
    footer: (currentPage: number, pageCount: number) => ({
      columns: [
        `Generated @ ${formatDatetime(
          loadedAt.value
        )} by ${me.value?.userId.toUpperCase()}`,
        { text: `Pg ${currentPage} of ${pageCount}`, alignment: "right" },
      ],
      margin: [20, 20],
    }),
    content: [
      {
        table: {
          layout: "lightHorizontalLines", // optional
          // headers are automatically repeated if the table spans over multiple pages
          // you can declare how many rows should be treated as headers
          headerRows: 1,
          widths: ["*", "*", "*", "*"],
          body: [
            [
              { text: "Number", alignment: "center" },
              { text: "Big", alignment: "right" },
              { text: "Small", alignment: "right" },
              { text: "Amount", alignment: "right" },
            ],
            ...selectedStake.value.map((stake) => [
              { text: stake.number, alignment: "center" },
              { text: formatCount(stake.big), alignment: "right" },
              { text: formatCount(stake.small), alignment: "right" },
              { text: formatDollar(stake.amount), alignment: "right" },
            ]),
            [
              {
                text: `Total (${
                  stakeTotal.value?.[selectedBetTypeId.value].count || 0
                })`,
                alignment: "center",
              },
              {
                text: formatCount(
                  stakeTotal.value?.[selectedBetTypeId.value].big || 0
                ),
                alignment: "right",
              },
              {
                text: formatCount(
                  stakeTotal.value?.[selectedBetTypeId.value].small || 0
                ),
                alignment: "right",
              },
              {
                text: formatDollar(
                  stakeTotal.value?.[selectedBetTypeId.value].amount || 0
                ),
                alignment: "right",
              },
            ],
          ],
        },
      },
    ],
  };
  (pdfMake as any).createPdf(docDef, null, null, vfs).download(fileName + ".pdf");
}


function formatDate(dateStr: string) {
  return customMoment(dateStr).format("ddd, DD/MM/YYYY");
}
function formatDatetime(date: Date) {
  return customMoment(date).format("ddd, DD/MM/YYYY hh:mm:ss A");
}

return { __sfc: true,route, $t, vfs, selectedNumber, showNumberStakeModal, selectedStartReleaseDate, selectedStartReleaseDateDate, result, loading, refetch, loadedAt, meResult, refetchMe, loadingMe, me, numberFilter, scrollArea, contentArea, generatingPdf, clusterize, sortMode, selectedBetTypeId, betTypes, stakeFile, selectedBetType, selectedStake, sortedRows, renderedRows, doClusterize, handleSelectNumber, stakeTotal, exportCsv, exportPdf, formatDate, formatDatetime, formatDollar, NumberStakeModal, formatCount }
}

})